import { useContext, useEffect, useState } from 'react';
import { LoginContext } from '../../contexts/LoginContext';
import { firstDayMonth, YMD_FORMAT, todayDate, lastTwoMonth, currentDateForFile } from '../../utils/DateUtils';
import { AppContext } from '../../contexts/AppContext';
import moment from 'moment';
import CheckingAccountAPI from '../../services/CheckingAccountAPI';
import { decodeFileXLSX } from '../../utils/MiscellaneousUtils';
import { ExpandLess } from '@material-ui/icons';

const SEARCH_TYPES = {
  LAST_2_MONTHS: 1,
  OUTSTANDING_BALANCE: 2,
  BY_DATE: 3,
};

const useCheckingAccountPresenter = tableRef => {
  const { isSupplier, idSupplier } = useContext(LoginContext);
  const { showError, showWarning } = useContext(AppContext);

  const [firstLoad, setFirstLoad] = useState(true);
  const [loadingPending, setLoadingPending] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [blockedByLoading, setBlockedByLoading] = useState(false);
  const [blockedByDateSelection, setBlockedByDateSelection] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [dateFrom, setDateFrom] = useState(isSupplier ? firstDayMonth() : null);
  const [dateTo, setDateTo] = useState(isSupplier ? moment() : null);
  const [searchType, setSearchType] = useState(SEARCH_TYPES.LAST_2_MONTHS);
  const [pendingBillsRows, setPendingBillsRows] = useState([]);
  const [summary, setSummary] = useState(null);

  const [triggerNewSearch, setTriggerNewSearch] = useState(true);
  const [oldSearchRequest, setOldSearchRequest] = useState({});

  const [loadingExport, setLoadingExport] = useState(false);

  useEffect(() => {
    if (!firstLoad) {
      if (searchType !== SEARCH_TYPES.BY_DATE) {
        tableRef.current.onChangePage(null, 0);
      } else {
        setBlockedByDateSelection(true);
      }
    }
  }, [searchType]);

  const cleanFilters = () => {
    setSelectedSupplier(null);
    setDateFrom(null);
    setDateTo(null);
  };

  const searchAccountStatement = (page, pageSize) => {
    setBlockedByLoading(true);
    blockedByDateSelection && setBlockedByDateSelection(false);

    const request = triggerNewSearch
      ? {
          cuit: isSupplier ? idSupplier : selectedSupplier.cuit,
          startDate: searchType == SEARCH_TYPES.BY_DATE ? dateFrom.format(YMD_FORMAT) : undefined,
          endDate: searchType == SEARCH_TYPES.BY_DATE ? dateFrom.format(YMD_FORMAT) : undefined,
          searchType: searchType,
          pageNumber: page,
          pageSize: pageSize,
        }
      : {
          ...oldSearchRequest,
          startDate: searchType == SEARCH_TYPES.BY_DATE ? dateFrom.format(YMD_FORMAT) : undefined,
          endDate: searchType == SEARCH_TYPES.BY_DATE ? dateTo.format(YMD_FORMAT) : undefined,
          searchType: searchType,
          pageNumber: page,
          pageSize: pageSize,
        };
        if (triggerNewSearch) {
          if (searchType != SEARCH_TYPES.BY_DATE) {
            setDateFrom(null);
            setDateTo(null);
          }
          setOldSearchRequest(request);
          setTriggerNewSearch(false);
        }
    
    return CheckingAccountAPI.getAccountStatement(request)
      .then(res => res)
      .catch(error => {
        const errorMsg = 'Error al obtener el estado de cuenta';
        showError(errorMsg, error);
        throw new Error(errorMsg);
      })
      .finally(() => {
        setBlockedByLoading(false);
      });
  };

  const searchPendingBills = () => {
    setLoadingPending(true);

    CheckingAccountAPI.getPendingBills(isSupplier ? idSupplier : selectedSupplier.cuit)
      .then(res => setPendingBillsRows(res.data))
      .catch(error => {
        const errorMsg = 'Error al obtener el estado de cuenta';
        showError(errorMsg, error);
      })
      .finally(() => setLoadingPending(false));
  };

  const searchSummary = () => {
    setLoadingSummary(true);

    CheckingAccountAPI.getSummary(isSupplier ? idSupplier : selectedSupplier.cuit)
      .then(res => setSummary(res))
      .catch(error => {
        const errorMsg = 'Error al obtener el estado de cuenta';
        showError(errorMsg, error);
      })
      .finally(() => setLoadingSummary(false));
  };

  const validateFilters = () => {
    let errors = [];
    const errorMessages = [];
    let i = 0;

    if ((errors[i++] = !isSupplier && !selectedSupplier)) errorMessages.push('Debe seleccionar un proveedor para iniciar la búsqueda');
    if (searchType == SEARCH_TYPES.BY_DATE) {
      if ((errors[i++] = !dateFrom && !dateTo)) errorMessages.push('Debe completar el rango de fechas');
      if ((errors[i++] = dateFrom && !dateTo)) errorMessages.push('Debe completar el rango de fechas o quitar la Fecha desde');
      if ((errors[i++] = !dateFrom && dateTo)) errorMessages.push('Debe completar el rango de fechas o quitar la Fecha hasta');
    }

    const hasErrors = errors.some(e => e);

    hasErrors && showWarning(errorMessages);

    return !hasErrors;
  };

  const validateAndSearch = () => {
    if (validateFilters()) {
      if (firstLoad) {
        setFirstLoad(false);
      } else {
        setTriggerNewSearch(true);
        tableRef.current.onChangePage(null, 0);
      }

      searchPendingBills();
      searchSummary();
    }
  };

  const validateSearchTypeFilters = () => {
    let errors = [];
    const errorMessages = [];
    let i = 0;

    if (searchType == SEARCH_TYPES.BY_DATE) {
      if ((errors[i++] = !dateFrom && !dateTo)) errorMessages.push('Debe completar el rango de fechas');
      if ((errors[i++] = dateFrom && !dateTo)) errorMessages.push('Debe completar el rango de fechas o quitar la Fecha desde');
      if ((errors[i++] = !dateFrom && dateTo)) errorMessages.push('Debe completar el rango de fechas o quitar la Fecha hasta');
    }

    const hasErrors = errors.some(e => e);

    hasErrors && showWarning(errorMessages);

    return !hasErrors;
  };

  const validateSearchTypeAndSearch = () => validateSearchTypeFilters() && tableRef.current.onChangePage(null, 0);

  const rows = query =>
    new Promise((resolve, reject) => {
      searchAccountStatement(query.page + 1, query.pageSize)
        .then((r)=>{
          const dataResult = r.data;
          const sortDataResult = dataResult.sort((a,b)=>{
            return new Date(b.documentDate) - new Date(a.documentDate);
          })
          resolve({
            data: sortDataResult,
            page: query.page,
            totalCount: r.totalCount,
          })
        })
        .catch(error => reject(error));
    });
    
  useEffect(() => {
    if (isSupplier) {
      setFirstLoad(false);
      searchPendingBills();
      searchSummary();
    }
  }, []);
  
  const handleExport = () => {
    setLoadingExport(true);

    const requestExcel = 
    {
      ...oldSearchRequest,
      startDate: searchType == SEARCH_TYPES.BY_DATE ? dateFrom.format(YMD_FORMAT) : undefined,
      endDate: searchType == SEARCH_TYPES.BY_DATE ? dateTo.format(YMD_FORMAT) : undefined,
      searchType: searchType,
      pageNumber: 0,
      pageSize: 0,
    }
    
    return CheckingAccountAPI.getExcel(requestExcel)
      .then(res => decodeFileXLSX(res,`Estado_De_Cuenta_${currentDateForFile()}.xlsx`))
      .catch(error => showError('No se pudo descargar el archivo solicitado'))
      .finally(() => setLoadingExport(false));
  };

  return {
    filtersProps: {
      selectedSupplier,
      setSelectedSupplier,
      validateAndSearch,
      cleanFilters,
    },
    postedBillsActions: {
      SEARCH_TYPES,
      dateFrom,
      setDateFrom,
      dateTo,
      setDateTo,
      searchType,
      setSearchType,
      validateSearchTypeAndSearch,
      blockedByLoading,
      handleExport,
      loadingExport,
    },
    summary,
    postedBillsRows: rows,
    blockedByDateSelection,
    pendingBillsRows,
    firstLoad,
    loadingPending,
    loadingSummary,
  };
};

export default useCheckingAccountPresenter;
